import React, { Component, createRef } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import Header from '../Header';
import MessageHistory from '../MessageHistory';
import ChatInput from '../ChatInput';
import withIsMobile from '../../context/withIsMobile';
import withConfig from '../../context/withConfig';
import Geolocation from '../Geolocation';
import UploadButton from '../UploadButton';
import OfflineMessage from '../MetaMessages/OfflineMessage';
import TimeoutOverlay from './TimeoutOverlay';
import WarningBeforeTimeout from './WarningBeforeTimeout';
import EndConfirmDialog from './EndConfirmDialog';
import { scrolledToRequestPreviousActivities, clearHistory } from '../../reducers/history/actions';
import { loadBrandText, setShowIntro } from '../../reducers/ui/actions';
import { visibleActivities } from '../../reducers/history/selectors';
import { hideChatInputFiled } from '../../components/MessageHistory/BotMessage/utils';
import IntroOverlay from './IntroOverlay';

const ChatPanelWrapper = withConfig(
  withIsMobile(styled.div`
    background-color: ${props => props.theme.backgroundColor};
    box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    height: 100vh;

    ${props =>
      !props.isMobile &&
      !props.config.inline &&
      `
    border-radius: 16px;
    margin: 15px;
    height: calc(100vh - 30px);
  }`};
  `)
);

const BrandTag = styled.div`
  padding: 3px 0 4px 0;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-style: italic;
  color: darkslategray;
  text-align: center;
  a,
  a:link,
  a:visited,
  a:hover,
  a:active {
    text-decoration: none;
    color: inherit;
  }
`;

let componentMounted = false;

class ChatPanel extends Component {
  constructor(props) {
    super(props);
    this.timeoutOverlayWindowRef = createRef();
    this.timeoutHeaderWindowRef = createRef();

    this.state = {
      showTimeoutOverlay: false,
      showModalConfirm: false,
      timerVariable: null,
      isIntroTransitioning: false,
      buttonCentered: false
    };
    this.setShowTimeoutOverlay = this.setShowTimeoutOverlay.bind(this);
    this.setShowModalConfirm = this.setShowModalConfirm.bind(this);
  }

  componentDidMount() {
    const { loadBrandText } = this.props;
    loadBrandText();

    window.addEventListener('message', e => {
      if (e.data.type === 'closeIntro') {
        this.props.setShowIntro(false);
      }
    });
  }

  componentWillMount() {
    const { clearHistory } = this.props;

    if (componentMounted) return;

    window.addEventListener('message', e => {
      if (e.data.type === 'clearHistory') {
        // Clear the history in the Redux store
        clearHistory();
      }
    });

    componentMounted = true;
  }

  setShowTimeoutOverlay(show) {
    this.setState({ showTimeoutOverlay: show });
  }

  setShowModalConfirm(show) {
    this.setState({ showModalConfirm: show });
  }

  setTimerVariable = timerVar => {
    this.setState({ timerVariable: timerVar });
  };

  handleIntroClose = () => {
    this.setState({ isIntroTransitioning: true });
    this.props.setShowIntro(false);
    // Reset the animation state after animation completes
    setTimeout(() => {
      this.setState({ isIntroTransitioning: false });
    }, 400); // Match the animation duration
  };

  handleButtonCenteredChange = isCentered => {
    this.setState({ buttonCentered: isCentered });
  };

  render() {
    const {
      online,
      activities,
      paginationData,
      isFetchingPreviousActivities,
      onLoadPreviousActivities,
      user,
      brandText,
      brandLink,
      config,
      showChatInput,
      isMobile,
      showIntro,
      skipTranslation,
      fieldDisabled
    } = this.props;

    const { isIntroTransitioning, buttonCentered } = this.state;
    const enabledTimeout = config.session && config.session.enabled;

    return (
      <div>
        {online &&
          enabledTimeout && (
            <TimeoutOverlay
              overlayDiv={this.timeoutOverlayWindowRef}
              headerDiv={this.timeoutHeaderWindowRef}
              showOverlay={this.state.showTimeoutOverlay}
              setShowOverlay={this.setShowTimeoutOverlay}
              sessionConfig={config.session}
              clearHistory={this.props.clearHistory}
              activities={activities}
            />
          )}
        <EndConfirmDialog
          overlayDiv={this.timeoutOverlayWindowRef}
          headerDiv={this.timeoutHeaderWindowRef}
          showOverlay={this.state.showModalConfirm}
          setShowOverlay={this.setShowModalConfirm}
          clearHistory={this.props.clearHistory}
          activities={activities}
        />
        <ChatPanelWrapper data-test-type="chatPanel" ref={this.timeoutOverlayWindowRef}>
          <IntroOverlay show={showIntro} onCloseIntro={this.handleIntroClose} isMobile={isMobile} />
          <Header
            nodeRef={this.timeoutHeaderWindowRef}
            showOverlay={this.state.showTimeoutOverlay}
            showModalConfirm={this.state.showModalConfirm}
            setShowModalConfirm={this.setShowModalConfirm}
          />
          <OfflineMessage online={online} />
          {online &&
            enabledTimeout && (
              <WarningBeforeTimeout
                activities={activities}
                sessionConfig={config.session}
                showTimeoutOverlay={this.state.showTimeoutOverlay}
                setShowTimeoutOverlay={this.setShowTimeoutOverlay}
                setShowModalConfirm={this.setShowModalConfirm}
                timerVar={this.state.timerVariable}
                setTimerVar={this.setTimerVariable}
              />
            )}
          <MessageHistory
            user={user}
            activities={activities}
            onLoadPreviousActivities={onLoadPreviousActivities}
            hasPreviousActivitiesToLoad={paginationData.hasNext}
            isFetchingPreviousActivity={isFetchingPreviousActivities}
            isIntroTransitioning={isIntroTransitioning}
            buttonCentered={buttonCentered}
            hasHeader={true}
            chatInput={
              showChatInput && (
                <ChatInput
                  disabled={!online || this.state.showTimeoutOverlay || this.state.showModalConfirm}
                  skipTranslation={skipTranslation}
                  fieldDisabled={fieldDisabled}
                  onButtonCenteredChange={this.handleButtonCenteredChange}
                />
              )
            }
          />
          <Geolocation />
          <UploadButton />
          {brandText && (
            <BrandTag>
              {brandLink ? (
                <a href={brandLink} target="_blank">
                  {brandText}
                </a>
              ) : (
                brandText
              )}
            </BrandTag>
          )}
        </ChatPanelWrapper>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  online: state.connection.connectionStatus === 2,
  activities: visibleActivities(state),
  showChatInput: hideChatInputFiled(state.history.activities),
  paginationData: state.history.paginationData,
  isFetchingPreviousActivities: state.history.isFetchingPreviousActivities,
  user: state.connection.botConnection && state.connection.botConnection.user,
  brandText: state.ui.brandText,
  brandLink: state.ui.brandLink,
  isMobile: state.ui.isMobile,
  showIntro: state.ui.showIntro,
  skipTranslation: Boolean(
    state.history.activities &&
      Array.isArray(state.history.activities) &&
      state.history.activities.length &&
      state.history.activities[state.history.activities.length - 1].entities &&
      Array.isArray(state.history.activities[state.history.activities.length - 1].entities) &&
      state.history.activities[state.history.activities.length - 1].entities.length &&
      state.history.activities[state.history.activities.length - 1].entities.findIndex(
        item =>
          item && item.type === 'NodeMarker' && item.marker && item.marker.shouldSkipTranslation
      ) !== -1
  ),
  fieldDisabled: Boolean(
    state.history.activities &&
      Array.isArray(state.history.activities) &&
      state.history.activities.length &&
      state.history.activities[state.history.activities.length - 1].entities &&
      Array.isArray(state.history.activities[state.history.activities.length - 1].entities) &&
      state.history.activities[state.history.activities.length - 1].entities.length &&
      state.history.activities[state.history.activities.length - 1].entities.findIndex(
        item => item && item.type === 'NodeMarker' && item.marker && item.marker.shouldDisableInput
      ) !== -1
  )
});

export default connect(mapStateToProps, {
  onLoadPreviousActivities: scrolledToRequestPreviousActivities,
  clearHistory,
  loadBrandText,
  setShowIntro
})(withConfig(ChatPanel));



// WEBPACK FOOTER //
// ./src/components/ChatPanel/index.js